import mongoose, { Types } from 'mongoose';
import { TFunction } from 'i18next';
import { HttpException } from '../../../utils/exceptions/httpException';
import { status } from '../../../utils/helpers/api.responses';
import { RolesEnum } from '../../../Modules/Auth/interfaces/auth.interface';
import User from '../../../Modules/Auth/models/auth.model';
import Chat from '../models/chat.model';
import Message from '../models/message.model';
import Attachment from '../../../Modules/Attachment/models/attachment.model';
import { NotificationService } from '../../../Modules/Notification/services/notification.service';
import Container, { Service } from 'typedi';
import { PopulatedUser } from '../interfaces/message.interface';
import { aws } from '../../../config/index';

@Service()
export class ChatService {
    public notificationService = Container.get(NotificationService)


    public async createChat(participants: string[], createdBy: string, t: TFunction) {
        const session = await mongoose.startSession();
        let transactionCommitted = false
        try {
            session.startTransaction();

            const users = await User.find({ _id: { $in: participants } })
                .populate('roleId', 'name')
                .session(session)
                .lean();
            if (users.length !== 2) {
                throw new HttpException(status.BadRequest, t('General.invalid', { field: 'Exactly two participants required' }));
            }

            const roles = users.map(u => (u.roleId as any).name.toLowerCase());
            if (roles.every(role => role === RolesEnum.LABOUR.toLowerCase())) {
                throw new HttpException(status.Forbidden, t('Chat.labour_to_labour_forbidden'));
            }
            const sortedParticipants = [...participants].sort();
            const existingChat = await Chat.findOne({
                participants: { $all: sortedParticipants, $size: 2 },
                isDeleted: false,
            }).session(session).lean();
            if (existingChat) {
                throw new HttpException(status.BadRequest, t('General.already_exist', { field: t('Chat.chat') }));
            }
            console.log("existingChat", existingChat)

            const chat = new Chat({
                participants: sortedParticipants,
                createdBy,
            });

            await chat.save({ session });
            await session.commitTransaction();
            transactionCommitted = true

            // await this.notificationService.sendNotification(
            //     'chat',
            //     'new_chat',
            //     {
            //         from: new Types.ObjectId(createdBy),
            //         to: participants.filter(p => p !== createdBy).map(p => new Types.ObjectId(p)),
            //         entityData: {
            //             chatId: chat._id,
            //             participants,
            //         },
            //     },
            //     t
            // );

            return {
                _id: chat._id.toString(),
                participants: chat.participants.map(p => p.toString()),
                createdBy: chat.createdBy.toString(),
                created_at: chat.created_at,
            };
        } catch (error) {
            console.log(error)
            if (!transactionCommitted) {
                await session.abortTransaction();
            }
            throw error instanceof HttpException ? error : new HttpException(
                status.InternalServerError,
                t('General.errorCreating', { field: t('Chat.chat') })
            );
        } finally {
            await session.endSession();
        }
    }

    public async sendMessage(chatId: string, senderId: string, receiverId: string, content: string, t: TFunction, attachmentIds?: Types.ObjectId[]) {
        const session = await mongoose.startSession();
        let transactionCommitted = false
        try {
            session.startTransaction();
            const chat = await Chat.findOne({ _id: chatId, isDeleted: false })
                .populate('participants', 'roleId')
                .session(session)
                .lean<{ participants: PopulatedUser[] }>();
            if (!chat) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('Chat.chat') }));
            }
            const attachments = attachmentIds.length
                ? await Attachment.find({ _id: { $in: attachmentIds }, isDeleted: false })
                    .select('filename key mimetype fileSize thumbnailKey')
                    .session(session)
                    .lean()
                : [];
            if (attachmentIds.length && attachments.length !== attachmentIds.length) {
                throw new HttpException(status.BadRequest, t('General.invalid', { field: 'attachmentIds' }));
            }

            const sender = await User.findById(senderId)
                .populate('roleId', 'name')
                .select('firstName lastName email')
                .populate<{ profileImage: { _id: Types.ObjectId; filename: string; key: string; thumbnailKey?: string; fileSize: number } }>('profileImage')
                .session(session)
                .lean();
            if (!sender) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.user') }));
            }

            const participantIds = chat.participants.map(p => p._id.toString());
            console.log("participantIds", participantIds)
            console.log("!participantIds.includes(senderId)", participantIds.includes(senderId))
            console.log("!participantIds.includes(receiverId) ", participantIds.includes(receiverId))
            console.log(`senderId:${senderId} and receiverId:${receiverId}`)
            const receiver = await User.findById(receiverId)
                .select('firstName lastName email roleId')
                .populate('roleId', 'name')
                .populate<{ profileImage: { _id: Types.ObjectId; filename: string; key: string; thumbnailKey?: string; fileSize: number } }>('profileImage')
                .session(session)
                .lean();

            const senderRoleName = (sender.roleId as any)?.name;
            if (!senderRoleName) {
                throw new HttpException(status.InternalServerError, 'Sender role not found or not populated');
            }

            const senderRole = senderRoleName.toLowerCase();

            if (senderRole === RolesEnum.LABOUR.toLowerCase()) {
                // const receiver = chat.participants.find(p => p._id.toString() === receiverId);

                const receiverRoleName = (receiver?.roleId as any)?.name;
                if (!receiverRoleName) {
                    throw new HttpException(status.InternalServerError, 'Receiver role not found or not populated');
                }

                const receiverRole = receiverRoleName.toLowerCase();
                if (receiverRole === RolesEnum.LABOUR.toLowerCase()) {
                    throw new HttpException(status.Forbidden, t('Chat.labour_to_labour_forbidden'));
                }
            }



            const message = new Message({
                chatId,
                senderId,
                receiverId,
                content,
                attachments: attachmentIds,
                isRead: false,
            });
            await message.save({ session });
            await Attachment.updateMany(
                { _id: { $in: attachmentIds }, isTemporary: true },
                { $set: { isTemporary: false } },
                { session }
            );

            // const signedAttachments = await Promise.all(
            //     attachments.map(async (a) => {
            //         const url = await getSignedUrl(s3, new GetObjectCommand({
            //             Bucket: process.env.AWS_S3_BUCKET,
            //             Key: a.key,
            //         }), { expiresIn: 3600 }); // 1 hour expiry
            //         const thumbnailUrl = a.thumbnailKey
            //             ? await getSignedUrl(s3, new GetObjectCommand({
            //                 Bucket: process.env.AWS_S3_BUCKET,
            //                 Key: a.thumbnailKey,
            //             }), { expiresIn: 3600 })
            //             : null;
            //         return {
            //             attachmentId: a._id.toString(),
            //             filename: a.filename,
            //             url,
            //             mimetype: a.mimetype,
            //             size: a.fileSize,
            //             thumbnailUrl,
            //         };
            //     })
            // );
            const senderProfileImage = sender.profileImage
                ? {
                    _id: sender.profileImage._id.toString(),
                    filename: sender.profileImage.filename,
                    url: `${aws.S3_BASE_URL}/${sender.profileImage.key}`,
                    thumbnailUrl: sender.profileImage.thumbnailKey
                        ? `${aws.S3_BASE_URL}/${sender.profileImage.thumbnailKey}`
                        : null,
                    fileSize: sender.profileImage.fileSize
                }
                : null;

            // Format receiver's profileImage
            const receiverProfileImage = receiver.profileImage
                ? {
                    _id: receiver.profileImage._id.toString(),
                    filename: receiver.profileImage.filename,
                    url: `${aws.S3_BASE_URL}/${receiver.profileImage.key}`,
                    thumbnailUrl: receiver.profileImage.thumbnailKey
                        ? `${aws.S3_BASE_URL}/${receiver.profileImage.thumbnailKey}`
                        : null,
                    fileSize: receiver.profileImage.fileSize
                }
                : null;
            if (!receiver) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.user') }));
            }
            await session.commitTransaction();
            transactionCommitted = true
            // await this.notificationService.sendNotification(
            //     'chat',
            //     'new_message',
            //     {
            //         from: new Types.ObjectId(senderId),
            //         to: [new Types.ObjectId(receiverId)],
            //         entityData: {
            //             chatId,
            //             messageId: message._id,
            //             content: content || 'Attachment sent',
            //             senderName: `${sender.firstName || ''} ${sender.lastName || ''}`.trim() || sender.email || 'Unknown',
            //             receiverName: `${receiver.firstName || ''} ${receiver.lastName || ''}`.trim() || receiver.email || 'Unknown',
            //             senderId,
            //             receiverId,
            //             isRead: message.isRead,
            //             attachments: attachments.map(a => ({
            //                 attachmentId: a._id.toString(),
            //                 filename: a.filename,
            //                 url: `https://${process.env.AWS_S3_BUCKET}.s3.amazonaws.com/${a.key}`,
            //                 mimetype: a.mimetype,
            //                 size: a.fileSize,
            //                 thumbnailUrl: a.thumbnailKey ? `https://${process.env.AWS_S3_BUCKET}.s3.amazonaws.com/${a.thumbnailKey}` : null,
            //             })),
            //         },
            //     },
            //     t
            // );

            return {
                messageId: message._id.toString(),
                chatId: message.chatId.toString(),
                senderId: message.senderId.toString(),
                receiverId: message.receiverId.toString(),
                content: message.content || null,
                attachments: attachments.map(a => ({
                    attachmentId: a._id.toString(),
                    filename: a.filename,
                    url: `https://${process.env.AWS_S3_BUCKET}.s3.amazonaws.com/${a.key}`,
                    mimetype: a.mimetype,
                    fileSize: a.fileSize,
                    thumbnailUrl: a.thumbnailKey ? `https://${process.env.AWS_S3_BUCKET}.s3.amazonaws.com/${a.thumbnailKey}` : null,
                })),
                isRead: message.isRead,
                created_at: message.created_at,
                sender: {
                    userId: senderId,
                    firstName: sender.firstName || 'Unknown',
                    lastName: sender.lastName || '',
                    email: sender.email || null,
                    profileImage: senderProfileImage,
                },
                receiver: {
                    userId: receiverId,
                    firstName: receiver.firstName || 'Unknown',
                    lastName: receiver.lastName || '',
                    email: receiver.email || null,
                    profileImage: receiverProfileImage,
                },
                // signedAttachments

            };
        } catch (error) {
            console.log(error)
            if (!transactionCommitted) {
                await session.abortTransaction();
            }
            throw error instanceof HttpException ? error : new HttpException(
                status.InternalServerError,
                t('General.errorCreating', { field: t('Message.message') })
            );
        } finally {
            await session.endSession();
        }
    }
    public async getChatsWithUser(
        userId: string,
        targetUserId: string,
        page: number = 1,
        limit: number = 20,
        t: TFunction,
        connectedUsers: Set<string> = new Set()
    ) {
        try {
            // Validate input
            if (!Types.ObjectId.isValid(userId) || !Types.ObjectId.isValid(targetUserId)) {
                throw new HttpException(status.BadRequest, t('General.invalid', { field: 'userId or targetUserId' }));
            }
            if (userId === targetUserId) {
                throw new HttpException(status.BadRequest, t('General.invalid', { field: 'Cannot chat with self' }));
            }

            // Check if target user exists and get roles
            const [authUser, targetUser] = await Promise.all([
                User.findById(userId).populate('roleId', 'name').lean(),
                User.findById(targetUserId).populate('roleId', 'name').lean(),
            ]);
            if (!authUser || !targetUser) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.user') }));
            }

            // Enforce LABOUR-to-LABOUR restriction
            const authRole = (authUser.roleId as any).name.toLowerCase();
            const targetRole = (targetUser.roleId as any).name.toLowerCase();
            if (authRole === RolesEnum.LABOUR.toLowerCase() && targetRole === RolesEnum.LABOUR.toLowerCase()) {
                throw new HttpException(status.Forbidden, t('Chat.labour_to_labour_forbidden'));
            }

            // Find chats where both users are participants
            const skip = (page - 1) * limit;
            const sortedParticipants = [userId, targetUserId].sort();
            const [chats, totalChatCount] = await Promise.all([
                Chat.find({
                    participants: { $all: sortedParticipants, $size: 2 },
                    isDeleted: false,
                })
                    .populate({
                        path: 'participants',
                        select: 'firstName lastName email profileImage',
                        populate: {
                            path: 'profileImage',
                            select: 'filename key thumbnailKey fileSize',
                        },
                    })
                    .sort({ created_at: -1 })
                    .lean(),
                Chat.countDocuments({
                    participants: { $all: sortedParticipants, $size: 2 },
                    isDeleted: false,
                }),
            ]);

            // Fetch messages for each chat with pagination
            const chatIds = chats.map((chat) => chat._id);
            const [messages, totalMessageCount] = await Promise.all([
                Message.find({ chatId: { $in: chatIds }, isDeleted: false })
                    .populate<{
                        senderId: { firstName: string; lastName: string; email: string; profileImage: any; _id: string };
                    }>('senderId', 'firstName lastName email profileImage _id')
                    .populate<{
                        receiverId: { firstName: string; lastName: string; email: string; profileImage: any; _id: string };
                    }>('receiverId', 'firstName lastName email profileImage _id')
                    .populate<{
                        attachments: { _id: any; filename: string; key: string; mimetype: string; fileSize: number; thumbnailKey?: string }[];
                    }>('attachments', 'filename key mimetype fileSize thumbnailKey')
                    .sort({ created_at: -1 })
                    .skip(skip)
                    .limit(limit)
                    .lean(),
                Message.countDocuments({ chatId: { $in: chatIds }, isDeleted: false }),
            ]);

            // Mark messages as read for the authenticated user
            await Message.updateMany(
                {
                    chatId: { $in: chatIds },
                    isDeleted: false,
                    receiverId: userId,
                    isRead: false,
                },
                { $set: { isRead: true } }
            );

            // Format messages
            const formattedMessages = messages.map((m) => ({
                messageId: m._id.toString(),
                chatId: m.chatId.toString(),
                sender: {
                    userId: m.senderId._id.toString(),
                    firstName: m.senderId.firstName || 'Unknown',
                    lastName: m.senderId.lastName || '',
                    email: m.senderId.email || null,
                    profileImage: (m as any).profileImage
                        ? {
                            _id: (m as any).profileImage._id.toString(),
                            filename: (m as any).profileImage.filename,
                            url: `${aws.S3_BASE_URL}/${(m as any).profileImage.key}`,
                            fileSize: (m as any).profileImage.fileSize,
                            thumbnailUrl: (m as any).profileImage.thumbnailKey ? `${aws.S3_BASE_URL}/${(m as any).profileImage.thumbnailKey}` : null,
                        }
                        : null,
                },
                receiver: {
                    userId: m.receiverId._id.toString(),
                    firstName: m.receiverId.firstName || 'Unknown',
                    lastName: m.receiverId.lastName || '',
                    email: m.receiverId.email || null,
                    profileImage: (m as any).profileImage
                        ? {
                            _id: (m as any).profileImage._id.toString(),
                            filename: (m as any).profileImage.filename,
                            url: `${aws.S3_BASE_URL}/${(m as any).profileImage.key}`,
                            fileSize: (m as any).profileImage.fileSize,
                            thumbnailUrl: (m as any).profileImage.thumbnailKey ? `${aws.S3_BASE_URL}/${(m as any).profileImage.thumbnailKey}` : null,
                        }
                        : null,
                },
                content: m.content || null,
                attachments: m.attachments?.map((a) => ({
                    attachmentId: a._id.toString(),
                    filename: a.filename,
                    url: `${aws.S3_BASE_URL}/${a.key}`,
                    mimetype: a.mimetype,
                    fileSize: a.fileSize,
                    thumbnailUrl: a.thumbnailKey ? `${aws.S3_BASE_URL}/${a.thumbnailKey}` : null,
                })) || [],
                isRead: m.isRead,
                created_at: m.created_at,
            }));

            // Group messages by chatId
            const messagesByChat = formattedMessages.reduce((acc, message) => {
                if (!acc[message.chatId]) {
                    acc[message.chatId] = [];
                }
                acc[message.chatId].push(message);
                return acc;
            }, {} as { [chatId: string]: any[] });

            // Format chats with their messages
            const formattedChats = chats.map((chat) => ({
                chatId: chat._id.toString(),
                participants: chat.participants.map((p: any) => ({
                    userId: p._id.toString(),
                    firstName: p.firstName || 'Unknown',
                    lastName: p.lastName || '',
                    email: p.email || null,
                    profileImage: p.profileImage
                        ? {
                            _id: p.profileImage._id.toString(),
                            filename: p.profileImage.filename,
                            url: `${aws.S3_BASE_URL}/${p.profileImage.key}`,
                            fileSize: p.profileImage.fileSize,
                            thumbnailUrl: p.profileImage.thumbnailKey ? `${aws.S3_BASE_URL}/${p.profileImage.thumbnailKey}` : null,
                        }
                        : null,
                })),
                createdBy: chat.createdBy.toString(),
                created_at: chat.created_at,
                messages: messagesByChat[chat._id.toString()] || [],
                participantStatuses: chat.participants.reduce(
                    (acc, p) => ({
                        ...acc,
                        [p._id.toString()]: connectedUsers.has(p._id.toString()) ? 'online' : 'offline',
                    }),
                    {}
                ),
            }));

            // Notify senders of read messages
            const readMessages = formattedMessages.filter((m) => m.receiver.userId === userId && m.isRead);
            if (readMessages.length > 0) {
                const senderIds = [...new Set(readMessages.map((m) => m.sender.userId))];
                const [senderMap, receiver] = await Promise.all([
                    User.find({ _id: { $in: senderIds } })
                        .select('firstName lastName email')
                        .lean()
                        .then((users) => Object.fromEntries(users.map((u) => [u._id.toString(), u]))),
                    User.findById(userId).select('firstName lastName email').lean(),
                ]);

                const notificationPromises = readMessages.map((message) =>
                    this.notificationService.sendNotification(
                        'chat',
                        'message_read',
                        {
                            from: new Types.ObjectId(userId),
                            to: [new Types.ObjectId(message.sender.userId)],
                            entityData: {
                                chatId: message.chatId,
                                messageId: message.messageId,
                                isRead: true,
                                senderName: `${senderMap[message.sender.userId]?.firstName || ''} ${senderMap[message.sender.userId]?.lastName || ''
                                    }`.trim() || senderMap[message.sender.userId]?.email || 'Unknown',
                                receiverName: `${receiver?.firstName || ''} ${receiver?.lastName || ''}`.trim() || receiver?.email || 'Unknown',
                            },
                        },
                        t
                    )
                );

                await Promise.allSettled(notificationPromises);
            }

            return {
                chats: formattedChats,
                total: totalMessageCount,
                page,
                totalPage: Math.ceil(totalMessageCount / limit),
            };
        } catch (error) {
            console.log(error);
            throw error instanceof HttpException
                ? error
                : new HttpException(status.InternalServerError, t('General.errorFetching', { field: t('Chat.chats') }));
        }
    }
    public async getChatHistory(
        chatId: string,
        userId: string,
        page: number = 1,
        limit: number = 20,
        t: TFunction,
        connectedUsers: Set<string> = new Set()
    ) {
        try {
            const chat = await Chat.findOne({ _id: chatId, isDeleted: false }).lean();
            if (!chat) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('Chat.chat') }));
            }

            const participantIds = chat.participants.map((p) => p.toString());
            const stringUserId = userId.toString();

            const otherUserId = participantIds.find((p) => p !== stringUserId);
            const users = await User.find({ _id: { $in: [userId, otherUserId] } })
                .select('firstName lastName email profileImage')
                .populate({
                    path: 'profileImage',
                    select: '_id filename key thumbnailKey fileSize',
                })
                .lean();

            const userMap = Object.fromEntries(users.map((u) => [u._id.toString(), u]));

            const currentUser = userMap[userId];
            const otherUser = userMap[otherUserId || userId];

            const formatProfileImage = (img: any) => {
                if (img && typeof img === 'object' && 'filename' in img && 'fileSize' in img) {
                    return {
                        _id: img._id?.toString(),
                        filename: img.filename,
                        fileSize: img.fileSize,
                        url: `${aws.S3_BASE_URL}/${img.key}`,
                        thumbnailUrl: img.thumbnailKey ? `${aws.S3_BASE_URL}/${img.thumbnailKey}` : null,
                    };
                }
                return null;
            };

            const skip = (page - 1) * limit;
            const [messages, totalCount] = await Promise.all([
                Message.find({ chatId, isDeleted: false })
                    .populate({
                        path: 'senderId',
                        select: 'firstName lastName email profileImage',
                        populate: {
                            path: 'profileImage',
                            select: '_id filename key thumbnailKey fileSize',
                        },
                    })
                    .populate({
                        path: 'receiverId',
                        select: 'firstName lastName email profileImage',
                        populate: {
                            path: 'profileImage',
                            select: '_id filename key thumbnailKey fileSize',
                        },
                    })
                    .populate({
                        path: 'attachments',
                        select: '_id filename key mimetype fileSize',
                    })
                    .sort({ created_at: -1 })
                    .skip(skip)
                    .limit(limit)
                    .lean(),
                Message.countDocuments({ chatId, isDeleted: false }),
            ]);

            let formattedMessages: any[] = [];

            if (messages.length > 0) {
                formattedMessages = messages.map((m) => ({
                    messageId: m._id.toString(),
                    // chatId: m.chatId.toString(),
                    sender: {
                        userId: m.senderId._id.toString(),
                        firstName: (m.senderId as any).firstName || 'Unknown',
                        lastName: (m.senderId as any).lastName || '',
                        email: (m.senderId as any).email || null,
                        profileImage: formatProfileImage((m.senderId as any).profileImage),
                    },
                    receiver: {
                        userId: m.receiverId._id.toString(),
                        firstName: (m.receiverId as any).firstName || 'Unknown',
                        lastName: (m.receiverId as any).lastName || '',
                        email: (m.receiverId as any).email || null,
                        profileImage: formatProfileImage((m.receiverId as any).profileImage),
                    },
                    content: m.content || null,
                    attachments: m.attachments?.map((a) => ({
                        attachmentId: a._id.toString(),
                        filename: (a as any).filename,
                        url: `${process.env.AWS_S3_BASE_URL}/${(a as any).key}`,
                        mimetype: (a as any).mimetype,
                        fileSize: (a as any).fileSize,
                    })) || [],
                    isRead: m.isRead,
                    created_at: m.created_at,
                }));

                await Message.updateMany(
                    {
                        chatId,
                        isDeleted: false,
                        receiverId: userId,
                        isRead: false,
                    },
                    { $set: { isRead: true } }
                );

                // Notify read messages
                const readMessages = messages.filter((m) => m.receiverId._id.toString() === userId && m.isRead);
                if (readMessages.length > 0) {
                    const senderIds = [...new Set(readMessages.map((m) => m.senderId._id.toString()))];
                    const [senderMap, receiverUser] = await Promise.all([
                        User.find({ _id: { $in: senderIds } })
                            .select('firstName lastName email')
                            .lean()
                            .then((users) => Object.fromEntries(users.map((u) => [u._id.toString(), u]))),
                        User.findById(userId).select('firstName lastName email').lean(),
                    ]);

                    const notificationPromises = readMessages.map((message) => {
                        const sender = senderMap[message.senderId._id.toString()];
                        return this.notificationService.sendNotification(
                            'chat',
                            'message_read',
                            {
                                from: new Types.ObjectId(userId),
                                to: [new Types.ObjectId(message.senderId._id.toString())],
                                entityData: {
                                    chatId,
                                    messageId: message._id.toString(),
                                    isRead: true,
                                    senderName: `${sender?.firstName || ''} ${sender?.lastName || ''}`.trim() || sender?.email || 'Unknown',
                                    receiverName: `${receiverUser?.firstName || ''} ${receiverUser?.lastName || ''}`.trim() || receiverUser?.email || 'Unknown',
                                },
                            },
                            t
                        );
                    });

                    await Promise.allSettled(notificationPromises);
                }
            }

            const participantStatuses = chat.participants.reduce(
                (acc, p) => ({
                    ...acc,
                    [p.toString()]: connectedUsers.has(p.toString()) ? 'online' : 'offline',
                }),
                {}
            );

            return {
                formattedMessages,
                totalCount,
                chatId: chat._id,
                totalPage: Math.ceil(totalCount / limit),
                participantStatuses,
                sender: {
                    userId,
                    firstName: currentUser.firstName,
                    lastName: currentUser.lastName,
                    email: currentUser.email,
                    profileImage: formatProfileImage(currentUser.profileImage),
                },
                receiver: {
                    userId: otherUserId,
                    firstName: otherUser.firstName,
                    lastName: otherUser.lastName,
                    email: otherUser.email,
                    profileImage: formatProfileImage(otherUser.profileImage),

                },
            };
        } catch (error) {
            console.log(error);
            throw error instanceof HttpException
                ? error
                : new HttpException(status.InternalServerError, t('General.errorFetching', { field: t('Message.messages') }));
        }
    }

    // public async getChatHistory(chatId: string, userId: string, page: number = 1, limit: number = 20, t: TFunction, connectedUsers: Set<string> = new Set()) {
    //     try {


    //         const chat = await Chat.findOne({ _id: chatId, isDeleted: false })
    //             .lean();
    //         if (!chat) {
    //             throw new HttpException(status.NotFound, t('General.not_found', { field: t('Chat.chat') }));
    //         }



    //         const skip = (page - 1) * limit;
    //         const [messages, totalCount] = await Promise.all([
    //             Message.find({ chatId, isDeleted: false })
    //                 .populate({
    //                     path: 'senderId',
    //                     select: 'firstName lastName email profileImage',
    //                     populate: {
    //                         path: 'profileImage',
    //                         select: '_id filename key thumbnailKey',
    //                     },
    //                 })
    //                 .populate({
    //                     path: 'receiverId',
    //                     select: 'firstName lastName email profileImage',
    //                     populate: {
    //                         path: 'profileImage',
    //                         select: '_id filename key thumbnailKey',
    //                     },
    //                 })
    //                 .populate({
    //                     path: 'attachments',
    //                     select: '_id filename key mimetype size',
    //                 })
    //                 .sort({ created_at: -1 })
    //                 .skip(skip)
    //                 .limit(limit)
    //                 .lean(),
    //             Message.countDocuments({ chatId, isDeleted: false }),
    //         ]);
    //         let formattedMessages: any[] = [];
    //         if (messages.length > 0) {

    //             formattedMessages = messages.map(m => ({
    //                 messageId: m._id.toString(),
    //                 chatId: m.chatId.toString(),
    //                 sender: {
    //                     userId: m.senderId._id.toString(),
    //                     firstName: (m.senderId as any).firstName || 'Unknown',
    //                     lastName: (m.senderId as any).lastName || '',
    //                     email: (m.senderId as any).email || null,
    //                     profileImage: (m.senderId as any).profileImage && typeof (m.senderId as any).profileImage === 'object' && 'filename' in (m.senderId as any).profileImage
    //                         ? {
    //                             _id: (m.senderId as any).profileImage._id.toString(),
    //                             filename: (m.senderId as any).profileImage.filename,
    //                             url: `${aws.S3_BASE_URL}/${(m.senderId as any).profileImage.key}`,
    //                             thumbnailUrl: (m.senderId as any).profileImage.thumbnailKey
    //                                 ? `${aws.S3_BASE_URL}/${(m.senderId as any).profileImage.thumbnailKey}`
    //                                 : null,
    //                         }
    //                         : null,

    //                 },
    //                 receiver: {
    //                     userId: m.receiverId._id.toString(),
    //                     firstName: (m.receiverId as any).firstName || 'Unknown',
    //                     lastName: (m.receiverId as any).lastName || '',
    //                     email: (m.receiverId as any).email || null,
    //                     profileImage: (m as any).profileImage
    //                         ? {
    //                             _id: (m as any).profileImage._id.toString(),
    //                             filename: (m as any).profileImage.filename,
    //                             url: `${aws.S3_BASE_URL}/${(m as any).profileImage.key}`,
    //                             thumbnailUrl: (m as any).profileImage.thumbnailKey ? `${aws.S3_BASE_URL}/${(m as any).profileImage.thumbnailKey}` : null,
    //                         }
    //                         : null,
    //                 },
    //                 content: m.content || null,
    //                 attachments: m.attachments?.map(a => ({
    //                     attachmentId: a._id.toString(),
    //                     filename: (a as any).filename,
    //                     url: `${process.env.AWS_S3_BASE_URL}/${(a as any).key}`,
    //                     mimetype: (a as any).mimetype,
    //                     size: (a as any).size,
    //                 })) || [],
    //                 isRead: m.isRead,
    //                 created_at: m.created_at,
    //             }));
    //             await Message.updateMany(
    //                 {
    //                     chatId,
    //                     isDeleted: false,
    //                     receiverId: userId,
    //                     isRead: false,
    //                 },
    //                 { $set: { isRead: true } }
    //             );
    //             // Notify sender of read status
    //             const readMessages = messages.filter(m => m.receiverId._id.toString() === userId && m.isRead);
    //             if (readMessages.length > 0) {
    //                 const senderIds = [...new Set(readMessages.map(m => m.senderId._id.toString()))];
    //                 const [senderMap, receiver] = await Promise.all([
    //                     User.find({ _id: { $in: senderIds } }).select('firstName lastName email').lean().then(users =>
    //                         Object.fromEntries(users.map(u => [u._id.toString(), u]))
    //                     ),
    //                     User.findById(userId).select('firstName lastName email').lean()
    //                 ]);

    //                 const notificationPromises = readMessages.map(message => {
    //                     const sender = senderMap[message.senderId._id.toString()];
    //                     return this.notificationService.sendNotification(
    //                         'chat',
    //                         'message_read',
    //                         {
    //                             from: new Types.ObjectId(userId),
    //                             to: [new Types.ObjectId(message.senderId._id.toString())],
    //                             entityData: {
    //                                 chatId,
    //                                 messageId: message._id.toString(),
    //                                 isRead: true,
    //                                 senderName: `${sender?.firstName || ''} ${sender?.lastName || ''}`.trim() || sender?.email || 'Unknown',
    //                                 receiverName: `${receiver?.firstName || ''} ${receiver?.lastName || ''}`.trim() || receiver?.email || 'Unknown',
    //                             },
    //                         },
    //                         t
    //                     );
    //                 });

    //                 await Promise.allSettled(notificationPromises);
    //             }
    //         } else {
    //             const participantIds = chat.participants.map((p) => p.toString());
    //             const receiverId = userId;
    //             const senderId = participantIds.find((p) => p !== receiverId);

    //             const userIdsToFetch = new Set([...participantIds, receiverId, senderId].filter(Boolean));

    //             const users = await User.find({ _id: { $in: Array.from(userIdsToFetch) } })
    //                 .select('firstName lastName email profileImage')
    //                 .populate({
    //                     path: 'profileImage',
    //                     select: '_id filename key thumbnailKey',
    //                 })
    //                 .lean();

    //             console.log("users", users)
    //             const userMap = Object.fromEntries(users.map((u) => [u._id.toString(), u]));
    //             if (senderId && receiverId) {
    //                 const sender = userMap[senderId] || {
    //                     firstName: 'Unknown',
    //                     lastName: '',
    //                     email: null,
    //                     profileImage: null,
    //                 };
    //                 const receiver = userMap[receiverId] || {
    //                     firstName: 'Unknown',
    //                     lastName: '',
    //                     email: null,
    //                     profileImage: null,
    //                 };

    //                 const formatProfileImage = (img: any) => {
    //                     if (img && typeof img === 'object' && 'filename' in img) {
    //                         return {
    //                             _id: img._id?.toString(),
    //                             filename: img.filename,
    //                             url: `${aws.S3_BASE_URL}/${img.key}`,
    //                             thumbnailUrl: img.thumbnailKey ? `${aws.S3_BASE_URL}/${img.thumbnailKey}` : null,
    //                         };
    //                     }
    //                     return null;
    //                 };

    //                 formattedMessages = [
    //                     {
    //                         messageId: null,
    //                         chatId: chatId,
    //                         sender: {
    //                             userId: senderId,
    //                             firstName: sender.firstName,
    //                             lastName: sender.lastName,
    //                             email: sender.email,
    //                             profileImage: formatProfileImage(sender.profileImage),
    //                         },
    //                         receiver: {
    //                             userId: receiverId,
    //                             firstName: receiver.firstName,
    //                             lastName: receiver.lastName,
    //                             email: receiver.email,
    //                             profileImage: formatProfileImage(receiver.profileImage),
    //                         },
    //                         content: null,
    //                         attachments: [],
    //                         isRead: false,
    //                         created_at: null,
    //                     },
    //                 ];
    //             }
    //         }

    //         // for (const message of readMessages) {
    //         //     const sender = await User.findById(message.senderId).select('firstName lastName email').lean();
    //         //     const receiver = await User.findById(userId).select('firstName lastName email').lean();
    //         //     await this.notificationService.sendNotification(
    //         //         'chat',
    //         //         'message_read',
    //         //         {
    //         //             from: new Types.ObjectId(userId),
    //         //             to: [new Types.ObjectId(message.senderId._id.toString())],
    //         //             entityData: {
    //         //                 chatId,
    //         //                 messageId: message._id.toString(),
    //         //                 isRead: true,
    //         //                 senderName: `${sender?.firstName || ''} ${sender?.lastName || ''}`.trim() || sender?.email || 'Unknown',
    //         //                 receiverName: `${receiver?.firstName || ''} ${receiver?.lastName || ''}`.trim() || receiver?.email || 'Unknown',
    //         //             },
    //         //         },
    //         //         t
    //         //     );
    //         // }

    //         // Include online status of participants
    //         const participantStatuses = chat.participants.reduce((acc, p) => ({
    //             ...acc,
    //             [p.toString()]: connectedUsers.has(p.toString()) ? 'online' : 'offline',
    //         }), {});

    //         return {
    //             formattedMessages,
    //             totalCount,
    //             totalPage: Math.ceil(totalCount / limit),
    //             participantStatuses,
    //         };
    //     } catch (error) {
    //         console.log(error)
    //         throw error instanceof HttpException ? error : new HttpException(
    //             status.InternalServerError,
    //             t('General.errorFetching', { field: t('Message.messages') })
    //         );
    //     }
    // }

    public async getAllChats(userId: string, page: number = 1, limit: number = 20, t: TFunction, connectedUsers: Set<string> = new Set()) {

        try {
            const skip = (page - 1) * limit;
            // const [chats, totalCount] = await Promise.all([
            //     Chat.find({ participants: userId, isDeleted: false })
            //         .populate<{ participants: { _id: string; firstName: string; lastName: string; email: string; profileImage: any }[] }>(
            //             'participants',
            //             'firstName lastName email profileImage'
            //         )
            //         .sort({ created_at: -1 })
            //         .skip(skip)
            //         .limit(limit)
            //         .lean(),
            //     Chat.countDocuments({ participants: userId, isDeleted: false }),
            // ]);
            const [chats, totalCount] = await Promise.all([
                Chat.find({ isDeleted: false })
                    .populate({
                        path: 'participants',
                        select: 'firstName lastName email profileImage',
                        populate: {
                            path: 'profileImage',
                            select: 'filename key thumbnailKey fileSize',
                        },
                    })
                    .sort({ created_at: -1 })
                    .skip(skip)
                    .limit(limit)
                    .lean(),
                Chat.countDocuments({ isDeleted: false }),
            ]);

            // const formattedChats = chats.map(chat => ({
            //     chatId: chat._id.toString(),
            //     participants: chat.participants.map(p => ({
            //         userId: p._id.toString(),
            //         firstName: (p as any).firstName || 'Unknown',
            //         lastName: (p as any).lastName || '',
            //         email: (p as any).email || null,
            //         profileImage: (p as any).profileImage && 'filename' in (p as any).profileImage && 'key' in (p as any).profileImage
            //             ? {
            //                 _id: (p as any).profileImage._id.toString(),
            //                 filename: (p as any).profileImage.filename,
            //                 url: `https://${process.env.AWS_S3_BUCKET}.s3.amazonaws.com/${(p as any).profileImage.key}`,
            //                 thumbnailUrl: (p as any).profileImage.thumbnailKey
            //                     ? `https://${process.env.AWS_S3_BUCKET}.s3.amazonaws.com/${(p as any).profileImage.thumbnailKey}`
            //                     : null,
            //             }
            //             : null,
            //     })),
            //     createdBy: chat.createdBy.toString(),
            //     created_at: chat.created_at,
            //     participantStatuses: chat.participants.reduce((acc, p) => ({
            //         ...acc,
            //         [p._id.toString()]: connectedUsers.has(p._id.toString()) ? 'online' : 'offline',
            //     }), {}),
            // }));
            // const formattedChats = chats.map(chat => ({
            //     _id: chat._id.toString(),
            //     participants: chat.participants.map((p: any) =>
            //         typeof p === 'object' && '_id' in p ? p._id.toString() : p.toString()
            //     ),
            //     createdBy: chat.createdBy
            // }));
            const formattedChats = await Promise.all(
                chats.map(async chat => {
                    const lastMessage = await Message.findOne({ chatId: chat._id, isDeleted: false })
                        .sort({ created_at: -1 })
                        .select('content created_at senderId receiverId attachments') // adjust fields as needed
                        .lean();

                    return {
                        _id: chat._id.toString(),
                        participants: chat.participants.map((p: any) =>
                            typeof p === 'object' && '_id' in p ? p._id.toString() : p.toString()
                        ),
                        userDetails: chat.participants.map((p: any) => ({
                            userId: p._id.toString(),
                            firstName: p.firstName || 'Unknown',
                            lastName: p.lastName || '',
                            email: p.email || null,
                            profileImage: p.profileImage
                                ? {
                                    _id: p.profileImage._id.toString(),
                                    filename: p.profileImage.filename,
                                    url: `${aws.S3_BASE_URL}/${p.profileImage.key}`,
                                    fileSize: p.profileImage.fileSize,
                                    thumbnailUrl: p.profileImage.thumbnailKey ? `${aws.S3_BASE_URL}/${p.profileImage.thumbnailKey}` : null,
                                }
                                : null,
                        })),
                        createdBy: chat.createdBy,
                        lastMessage: lastMessage
                            ? {
                                content: lastMessage.content,
                                createdAt: lastMessage.created_at,
                                senderId: lastMessage.senderId.toString(),
                                receiverId: lastMessage.receiverId.toString(),
                                attachments: lastMessage.attachments?.map((a: any) => a.toString()) || [],
                            }
                            : null,
                    };
                })
            );


            return {
                formattedChats,
                totalCount,
                page,
                totalPage: Math.ceil(totalCount / limit),
            };
        } catch (error) {
            console.log(error)
            throw error instanceof HttpException ? error : new HttpException(
                status.InternalServerError,
                t('General.errorFetching', { field: t('Chat.chats') })
            );
        }
    }

    public async getChatById(chatId: string, userId: string, t: TFunction) {
        try {

            const chat = await Chat.findOne({ _id: chatId, isDeleted: false, participants: userId })
                .populate<{ participants: { _id: string; firstName: string; lastName: string; email: string; profileImage: any }[] }>(
                    'participants',
                    'firstName lastName email profileImage'
                )
                .lean();
            if (!chat) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('Chat.chat') }));
            }

            const formattedParticipants = chat.participants.map(p => ({
                userId: p._id.toString(),
                firstName: p.firstName || 'Unknown',
                lastName: p.lastName || '',
                email: p.email || null,
                profileImage: p.profileImage && 'filename' in p.profileImage && 'key' in p.profileImage && 'fileSize' in p.profileImage
                    ? {
                        _id: p.profileImage._id.toString(),
                        filename: p.profileImage.filename,
                        url: `https://${process.env.AWS_S3_BUCKET}.s3.amazonaws.com/${p.profileImage.key}`,
                        thumbnailUrl: p.profileImage.thumbnailKey
                            ? `https://${process.env.AWS_S3_BUCKET}.s3.amazonaws.com/${p.profileImage.thumbnailKey}`
                            : null,
                        fileSize: p.profileImage.fileSize,
                    }
                    : null,
            }));

            return {
                chatId: chat._id.toString(),
                participants: formattedParticipants,
                createdBy: chat.createdBy.toString(),
                created_at: chat.created_at,
            };
        } catch (error) {
            throw error instanceof HttpException ? error : new HttpException(
                status.InternalServerError,
                t('General.errorFetching', { field: t('Chat.chat') })
            );
        }
    }

    public async markMessageAsRead(messageId: string, userId: string, t: TFunction) {
        const session = await mongoose.startSession();
        let transactionCommitted = false
        try {
            session.startTransaction();

            const message = await Message.findOne({ _id: messageId, isDeleted: false })
                .populate('chatId', 'participants')
                .populate('senderId', 'firstName lastName email')
                .populate('receiverId', 'firstName lastName email')
                .session(session);
            if (!message) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('Message.message') }));
            }

            const chat = message.chatId as any;
            if (!chat.participants.map(p => p._id.toString()).includes(userId)) {
                throw new HttpException(status.Forbidden, t('Role.permission_denied'));
            }

            if (!message.isRead && message.receiverId.toString() === userId) {
                message.isRead = true;
                await message.save({ session });

                // await this.notificationService.sendNotification(
                //     'chat',
                //     'message_read',
                //     {
                //         from: new Types.ObjectId(userId),
                //         to: [new Types.ObjectId(message.senderId._id.toString())],
                //         entityData: {
                //             chatId: message.chatId.toString(),
                //             messageId: message._id.toString(),
                //             isRead: true,
                //             senderName: `${(message.senderId as any).firstName || ''} ${(message.senderId as any).lastName || ''}`.trim() || (message.senderId as any).email || 'Unknown',
                //             receiverName: `${(message.receiverId as any).firstName || ''} ${(message.receiverId as any).lastName || ''}`.trim() || (message.receiverId as any).email || 'Unknown',
                //         },
                //     },
                //     t
                // );
            }
            await session.commitTransaction();
            transactionCommitted = true

            return {
                messageId: message._id.toString(),
                chatId: ((message.chatId as any)?._id || message.chatId).toString(),
                isRead: message.isRead,
                updated_at: message.updated_at,
            };
        } catch (error) {
            if (!transactionCommitted) {
                await session.abortTransaction();
            }
            throw error instanceof HttpException ? error : new HttpException(
                status.InternalServerError,
                t('General.errorUpdating', { field: t('Message.message') })
            );
        } finally {
            await session.endSession();
        }
    }
}